var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React from 'react';
import { withTranslation } from 'react-i18next';
import { Route, Switch, withRouter } from 'react-router-dom';
import { withTheme, withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import OfflineBoltOutlined from '@material-ui/icons/OfflineBoltOutlined';
import Loading from '../../components/Loading';
import Footer from '../../components/Footer';
import Login from '../login/Login';
import { authInstance, appleProvider, googleProvider, facebookProvider } from '../../firebase';
import { getUrlParams } from '../../utils';
import AccountManager from '../../utils/classes/AccountManager';
import AccountProviders from '../../utils/classes/AccountProviders';
import Branch from '../../utils/classes/Branch';
import ConversationManager from '../../utils/classes/ConversationManager';
import FirebaseLogin from '../../utils/classes/FirebaseLogin';
import ParseUser from '../../utils/classes/ParseUser';
import { clearSearchValue, getSearchValue } from '../../utils/http';
import { getLanguageCode } from '../../utils/i18n';
import { AVA_URL } from '../../constants';
import Grid from '@material-ui/core/Grid';
import Testimonial from '../account/components/Signup/Testimonial';
import Explanation from '../account/components/Signup/Explanation';
import ScribeLoginEmail from './ScribeLoginEmail';
import ScribeLoginPhone from './ScribeLoginPhone';
import Card from '@material-ui/core/Card';
import { CardActions } from '@material-ui/core';
import { LoginFacebook } from '../../components/LoginFacebook';
import { LoginGoogle } from '../../components/LoginGoogle';
import { LoginApple } from '../../components/LoginApple';
import { LoginEmail } from '../../components/LoginEmail';
import { LoginPhone } from '../../components/LoginPhone';
const styles = (theme) => ({
    errorContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        paddingTop: 16,
        paddingBottom: 16,
    },
    message: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 44,
        '& > :not(:last-child)': {
            marginRight: 4,
        },
        [theme.breakpoints.down('sm')]: {
            marginBottom: 150,
        },
    },
    link: {
        color: theme.palette.ava.blue,
        '&:link, &:visited, &:hover, &:active': {
            textDecoration: 'none',
        },
        '&:hover': {
            color: theme.palette.ava.blue1,
        },
    },
    middleContainer: {
        paddingBottom: 48,
        paddingTop: 96,
        maxWidth: 600,
        marginLeft: 'auto',
        marginRight: 'auto',
        display: 'flex',
        flexDirection: 'column',
        width: '85%',
    },
    title: {
        fontWeight: 700,
        color: theme.palette.ava.selfColorBlue,
    },
});
class ScribeWelcome extends React.Component {
    constructor(props) {
        super(props);
        this.login = (provider) => {
            this.handleFirebaseAuthPromise(authInstance.signInWithPopup(provider));
        };
        this.loginWithToken = (token) => {
            this.handleFirebaseAuthPromise(this.getSignInWithTokenPromise(token));
        };
        this.handleFirebaseAuthPromise = (promise) => {
            if (!promise)
                return;
            this.setState({ loading: true });
            promise
                .then(() => {
                /**
                 * handleAuthStateChanged should actually handle the loading.
                 * But we're adding a 15s timer in case the loading fails to undo itself.
                 */
                setTimeout(() => {
                    this.setState({ loading: false });
                }, 15000);
            })
                .catch((err) => this.handleFirebaseLoginError(err));
        };
        this.getSignInWithTokenPromise = (firebaseCustomToken) => {
            const { setShowQuickSetup } = this.props;
            if (firebaseCustomToken) {
                // User signed up through Web and is now trying to launch the desktop app.
                const newUser = getSearchValue(window, 'newUser');
                if (window.isElectron && newUser) {
                    setShowQuickSetup(true);
                }
                return authInstance.signInWithCustomToken(firebaseCustomToken);
            }
            return undefined;
        };
        this.handleFirebaseLoginError = (error) => __awaiter(this, void 0, void 0, function* () {
            const { history, location } = this.props;
            this.setState({ loading: false });
            if (error.code === 'auth/account-exists-with-different-credential') {
                // TODO: This does not work as of 05/02
                const providers = yield authInstance.fetchSignInMethodsForEmail(error.email);
                const accountProviders = new AccountProviders();
                accountProviders.updateProviders(providers);
                this.setState({
                    accountExistsProviders: accountProviders.getProviders(),
                    accountExistsEmail: error.email,
                });
                if (location.pathname.split('/')[1] === 'web') {
                    history.push('/web/login/account-exists');
                }
            }
        });
        this.checkProvidersForPasswordReset = (email) => __awaiter(this, void 0, void 0, function* () {
            const { history, location } = this.props;
            const providers = yield authInstance.fetchSignInMethodsForEmail(email);
            if (providers.length === 0 || providers.includes('password')) {
                return false;
            }
            const accountProviders = new AccountProviders();
            accountProviders.updateProviders(providers);
            this.setState({
                accountExistsProviders: accountProviders.getProviders(),
                accountExistsEmail: email,
            });
            if (location.pathname.split('/')[1] === 'web') {
                history.push('/web/login/account-exists');
            }
            return true;
        });
        this.handleAuthStateChanged = (user) => __awaiter(this, void 0, void 0, function* () {
            const { branchId, electronAppLoaded, fetchUserProfile, firebaseLoginSuccess, history, needInternalAudioAccess, setAuthenticating, setElectronAppLoaded, initiateSession, } = this.props;
            if (!user && this.conversationManager.shouldSignInAnonymously()) {
                // We haven't logged in, but we're already in the process of signing in
                // anonymously, so in order not to confuse the user - keep showing the
                // loading circle.
                return;
            }
            // The entry point to this function is firebase login state change. The
            // outflow is either `initiateSession` which initializes the websocket, or
            // a redirect to a different page.
            this.firebaseLogin = new FirebaseLogin(user);
            setAuthenticating(false);
            if (!user || this.firebaseLogin.shouldWaitForEmailVerification) {
                // We treat unverified users the same as users who have not logged in.
                return this.setState({ loading: false });
            }
            this.firebaseLogin.onLoginSuccess(firebaseLoginSuccess);
            this.firebaseLogin.logUserSession();
            const parseUserProfile = yield fetchUserProfile(user.uid, user.isAnonymous);
            this.parseUser = new ParseUser(parseUserProfile);
            if (!this.parseUser.exists()) {
                // Since the above code should create Parse user in case it does not exist,
                // this if should never get triggered and most certainly signifies an error.
                // Keep in mind that also anonymous users have a Parse profile as of 05/16/2022
                return;
            }
            this.parseUser.setParseIdToLocalStorage();
            this.parseUser.setAvaIdToLocalStorage();
            if (this.firebaseLogin.isAnonymous) {
                // If the user is anonymous then there's two options:
                // * we treat them as logged out
                // * we send them directly to someone else's conversation.
                // That's handled in localStorage by either by ava_request or find_by_ava_name.
                const findByAvaName = localStorage.getItem('find_by_ava_name');
                if (findByAvaName) {
                    localStorage.removeItem('find_by_ava_name');
                    localStorage.setItem('ava_request', findByAvaName);
                }
                const avaRequest = localStorage.getItem('ava_request');
                if (avaRequest) {
                    // The avaRequest will be picked up through localStorage by ConversationManager
                    // when it mounts.
                    return initiateSession(history);
                }
                return this.setState({ loading: false });
            }
            if (!this.firebaseLogin.uid) {
                return this.setState({ loading: false });
            }
            // All the 'account' code was migrated from a different app that had
            // different login flow. In the end it would make sense to unify the
            // login flow, and get rid of the accountLogin action, but for now
            // (03/2021) keeping the actions as-is is the best way forward.
            // Future refactors very welcome.
            const { accountLogin, product } = this.props;
            if (product === 'account' && !this.firebaseLogin.isAnonymous) {
                accountLogin({
                    avatar: this.firebaseLogin.photoURL,
                    email: this.firebaseLogin.email,
                    id: this.firebaseLogin.uid,
                    name: this.firebaseLogin.displayName,
                }, user.getIdToken());
                // It's necessary to exit the function immediately upon account login
                // because otherwise, the code below will create a new user. Creating
                // a new user also happens in account logic. So without this, 2 parse
                // users will be created referencing the same email address, which will
                // cause errors due to Parse objectId mismatch.
                return;
            }
            if (this.firebaseLogin.isNew) {
                this.parseUser.trackSignUp();
            }
            else if (this.firebaseLogin.justSignedIn) {
                this.parseUser.trackSignIn();
            }
            // If the user has never set up their hearing profile, we want to redirect them
            // to the profile creation page.
            const profileSetupRedirectURL = this.getURLForProfileSetup(this.parseUser, history);
            if (profileSetupRedirectURL) {
                history.push(profileSetupRedirectURL);
                return;
            }
            // If the user was signing in during the elctron sign-in flow, we want to
            // redirect them.
            const queryParams = getUrlParams();
            if (!this.firebaseLogin.isAnonymous && (queryParams === null || queryParams === void 0 ? void 0 : queryParams.auth) === 'desktop') {
                // User has clicked 'Sign in with browser' in the desktop app. In this
                // case we have to redirect them back to a page where they can click a
                // button to redirect back to the desktop app.
                history.push('/web/login/desktop', { avaUser: this.parseUser.user });
                return;
            }
            // We need to figure out whether to start a conversation instantly or not.
            // TODO: This logic could be put in a separate function.
            let startConvo = false;
            if (history.location.search.includes('startConvo=true')) {
                // NOTE: This whole thing only works because ScribeLoginProfiling is
                // rendered outside of ScribeWelcome, so redirecting back to /web?startConvo
                // recreates and remounts ScribeWelcome and thus triggers handleAuthStateChanged.
                // So if everything is part of ScribeWelcome, this will never get triggered
                // and the new conversation will not get triggered.
                history.replace(history.location.pathname);
                startConvo = true;
            }
            if (user && !needInternalAudioAccess) {
                startConvo = true;
            }
            if (!branchId && window.isElectron && electronAppLoaded) {
                // Auto-start conversation on desktop app start
                // (except for one-click links -> for them the conversation will
                // auto start via another event)
                // Do not autostart on macOS if app was launched on login
                const launchedOnLogin = window.electronIPC.sendSyncCheckLaunchedOnLogin();
                startConvo = !launchedOnLogin;
                setElectronAppLoaded(false);
            }
            const features = this.parseUser.getFeatures();
            if (!window.isElectron && features && features.scribe) {
                // Never auto-start conversation for 'scribe' users on web
                startConvo = false;
            }
            return initiateSession(history, startConvo);
        });
        this.initializeConversationManager = () => {
            const { branchId, scribe, token } = this.props;
            this.conversationManager = new ConversationManager({ branchId, scribe, token });
            this.conversationManager.setTokenInURLIfExists();
            this.conversationManager.setAvaRequestIfExists();
            if (this.conversationManager.avaRoomRequest) {
                this.setState({ findByAvaName: this.conversationManager.avaRoomRequest });
            }
            if (this.conversationManager.shouldSignInAnonymously()) {
                authInstance.signInAnonymously();
            }
        };
        this.initializeBranch = () => __awaiter(this, void 0, void 0, function* () {
            const { branchId, enableAudioMode, startRecord } = this.props;
            this.branch = new Branch(branchId);
            const branchInit = this.branch.initializeBranch();
            if (branchId)
                yield branchInit;
            if (this.branch.hasData()) {
                this.branch.savePropertiesToLocalStorageIfExists();
                if (this.branch.hasAudio()) {
                    enableAudioMode();
                    startRecord();
                }
            }
        });
        this.getAvaLanguageUrl = () => {
            const languageCode = getLanguageCode();
            if (languageCode !== 'en') {
                return `${AVA_URL}/${languageCode}`;
            }
            return AVA_URL;
        };
        this.state = {
            supportGuest: false,
            loading: true,
            findByAvaName: '',
            accountExistsEmail: '',
            accountExistsProviders: new AccountProviders().getProviders(),
            trackedLoggedIn: false,
            askedToStartConvo: false,
        };
    }
    componentWillReceiveProps(nextProps) {
        if (nextProps.backendSupport.includes('connectRequest')) {
            this.setState({ supportGuest: true });
        }
    }
    componentDidMount() {
        return __awaiter(this, void 0, void 0, function* () {
            const { fetchAsyncBackendIfNeeded, fetchBackendSupport, product } = this.props;
            // TODO - this is potentially slow
            fetchBackendSupport();
            fetchAsyncBackendIfNeeded();
            this.initializeConversationManager();
            yield this.initializeBranch();
            if (product === 'account') {
                this.accountManager = new AccountManager();
                this.accountManager.setAccountDetailsInLocalStorage();
            }
            this.firebaseListenerUnsubscribe = authInstance.onAuthStateChanged(this.handleAuthStateChanged);
            const firebaseCustomToken = getSearchValue(window, 'firebaseCustomToken');
            if (firebaseCustomToken) {
                clearSearchValue(window, 'firebaseCustomToken');
                this.loginWithToken(firebaseCustomToken);
            }
        });
    }
    // Returns URL to redirect the user for profile creation.
    // Returns undefined if there is no need for profile creation.
    getURLForProfileSetup(parseUser, history) {
        const { hearingProfile, parse: { organizationId, userName }, } = parseUser.user;
        if (hearingProfile !== -1 ||
            // The user has had their first conversation
            parseUser.hasHadFirstConvo() ||
            // or we already have all the necessary info
            (userName && organizationId) ||
            // or startConvo is included in the path and this means we should skip
            // the user profiling and go directly to conversation.
            history.location.search.includes('startConvo=true')) {
            return undefined;
        }
        // User has not had a conversation
        if (organizationId) {
            // Organization id exists, but not user name
            return `/web/login/profile/user-name-subscribed`;
        }
        else {
            // User name and organization do not exist
            return `/web/login/profile/user-name`;
        }
    }
    getProviderFromIdentifier(identifier) {
        let signUpMethod = identifier.replace('.com', ''); // Remove top level domain
        if (signUpMethod === 'password') {
            signUpMethod = 'email';
        }
        return signUpMethod.charAt(0).toUpperCase() + signUpMethod.slice(1);
    }
    componentWillUnmount() {
        if (this.firebaseListenerUnsubscribe) {
            this.firebaseListenerUnsubscribe();
        }
    }
    render() {
        const { backends, classes, displayable, error, toggleDevMode, t } = this.props;
        const { loading, accountExistsEmail, accountExistsProviders } = this.state;
        const productPath = location.pathname.split('/')[1];
        if (loading) {
            return React.createElement(Loading, null);
        }
        return (React.createElement("div", null,
            (error === null || error === void 0 ? void 0 : error.name) && (React.createElement("div", { className: classes.errorContainer },
                React.createElement(OfflineBoltOutlined, null),
                React.createElement(Typography, { variant: "subtitle1", align: "right", style: { marginLeft: 6 } }, error.name))),
            React.createElement(Switch, null,
                React.createElement(Route, { exact: true, path: ['/web', '/web/login'], render: () => {
                        return (React.createElement("div", null,
                            React.createElement(Login, { loading: loading, loginWithApple: () => this.login(appleProvider), loginWithGoogle: () => this.login(googleProvider), loginWithFacebook: () => this.login(facebookProvider), loginWithToken: (token) => this.loginWithToken(token), loggingIn: loading, productPath: productPath }),
                            React.createElement("div", { className: classes.message },
                                React.createElement("span", null, t('landing.agreement.use')),
                                React.createElement("a", { className: classes.link, target: "_blank", href: `${this.getAvaLanguageUrl()}/terms` }, t('landing.agreement.terms')),
                                React.createElement("span", null, t('landing.agreement.and')),
                                React.createElement("a", { className: classes.link, target: "_blank", href: `${`${this.getAvaLanguageUrl()}/privacy`}` }, t('landing.agreement.privacy')))));
                    } }),
                React.createElement(Route, { exact: true, path: ['/account/login'], render: () => {
                        return (React.createElement(React.Fragment, null,
                            React.createElement(Grid, { container: true },
                                React.createElement(Grid, { item: true, xs: 12, md: 4 },
                                    React.createElement(Testimonial, null)),
                                React.createElement(Grid, { item: true, xs: 12, md: 8, style: {
                                        minHeight: 'calc(100vh - 114px)',
                                    } },
                                    React.createElement(Explanation, { loading: loading, handleLoginFacebook: () => this.login(facebookProvider), handleLoginApple: () => this.login(appleProvider), handleLoginGoogle: () => this.login(googleProvider), loginPhoneLink: "/account/login/phone", loginEmailLink: "/account/login/email" })))));
                    } }),
                React.createElement(Route, { exact: true, path: ['/web/login/email', '/account/login/email'], render: () => (React.createElement(ScribeLoginEmail, { checkProvidersForPasswordReset: this.checkProvidersForPasswordReset.bind(this) })) }),
                React.createElement(Route, null,
                    React.createElement("div", { className: classes.middleContainer },
                        React.createElement("div", { style: { flexGrow: 1 } }),
                        React.createElement(Switch, null,
                            React.createElement(Route, { path: ['/web/login/phone', '/account/login/phone'], render: () => React.createElement(ScribeLoginPhone, null) }),
                            React.createElement(Route, { exact: true, path: ['/web/login/account-exists', '/account/login/account-exists'], render: () => {
                                    return (React.createElement(Card, { style: {
                                            boxShadow: 'none',
                                            marginBottom: '20px',
                                        } },
                                        React.createElement(CardActions, null,
                                            React.createElement("div", { style: {
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    margin: '0 auto 12px',
                                                    maxWidth: 380,
                                                } },
                                                React.createElement(Typography, { variant: "h4", className: classes.title, align: "center", gutterBottom: true }, t('signin-messages.accountExistsHead')),
                                                React.createElement(Typography, { variant: "body1", align: "center", gutterBottom: true }, t('signin-messages.accountExistsBody', { email: accountExistsEmail })),
                                                accountExistsProviders.facebook && (React.createElement(LoginFacebook, { loading: loading, login: () => this.login(facebookProvider) })),
                                                accountExistsProviders.google && (React.createElement(LoginGoogle, { loading: loading, login: () => this.login(googleProvider) })),
                                                accountExistsProviders.apple && (React.createElement(LoginApple, { loading: loading, login: () => this.login(appleProvider) })),
                                                accountExistsProviders.email && (React.createElement(LoginEmail, { to: loading ? {} : `/login/email`, loading: loading })),
                                                accountExistsProviders.phone && (React.createElement(LoginPhone, { to: loading ? {} : `/login/phone`, loading: loading }))))));
                                } }))))),
            error && error.message && (React.createElement(Typography, { variant: "subtitle1", align: "center" }, error.message && error.message)),
            React.createElement(Footer, { toggleDevMode: toggleDevMode, displayable: displayable, backends: backends })));
    }
}
export default withTranslation()(withTheme(withStyles(styles)(withRouter(ScribeWelcome))));
